import { FormControl, FormLabel } from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { getIn } from 'formik';
import React, { HTMLInputTypeAttribute } from 'react';
import { mascaraCep } from 'utils/format_cep';
import { mascaraCnpj, mascaraCpf, mascaraCpfCnpj } from 'utils/format_cpf';
import { monetario_input, monetario_output } from 'utils/format_monetario';
import { mascaraTelefone } from 'utils/format_phone';

interface InputComLabelProps {
  formik: any;
  label: string;
  labelTexto: string;
  placeholder?: string;
  width?: string;
  type?: HTMLInputTypeAttribute;
  required?: boolean;
  maxLength?: number;
  acceptFraction?: boolean;
  version?: 'primary' | 'secondary';
  mask?: 'cep' | 'telefone' | 'numero' | 'porcentagem' | 'dinheiro' | 'numerov2' | 'cpf' | 'cnpj' | 'cpfcnpj' | 'custom' | 'data' | null;
  customMask?: (value: string) => string;
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const mascaraPorcentagem = (valor: string) => {
  if (valor.length === 0) return '';
  const alterado = valor.replace('%', '');
  if (isNaN(Number(alterado))) return '';
  if (Number(alterado) > 100) return '100%';
  if (Number(alterado) < 0 || Number(alterado) === 0) return '0%';
  return alterado + '%';
};

const InputComLabelFormik: React.FC<InputComLabelProps> = ({
  formik,
  label,
  labelTexto,
  placeholder = '',
  width = '100%',
  type = 'text',
  maxLength = 200,
  required = false,
  version = 'secondary',
  mask = null,
  disabled = false,
  acceptFraction = true,
  customMask = null,
  onBlur,
}) => {
  // Função para obter o valor formatado para exibição
  const getDisplayValue = () => {
    const currentValue = getIn(formik.values, label);

    if (mask === 'dinheiro') {
      if (typeof currentValue === 'number') {
        return monetario_output(currentValue);
      }
      if (!currentValue) return '';
      return currentValue;
    }

    if (mask === 'porcentagem') {
      return mascaraPorcentagem(currentValue || '');
    }

    if (mask === 'numero') {
      if (!acceptFraction) {
        const numero = Number(currentValue);
        return String(numero);
      }

      return Math.floor(Number(currentValue)) || 0;

    }

    if (mask === 'numerov2') {
      if (!acceptFraction) {
        return Math.floor(Number(currentValue)) || 0;
      }
      return currentValue || '';
    }

    if (mask === 'cpf') {
      return mascaraCpf(currentValue || '');
    }

    if (mask === 'cnpj') {
      return mascaraCnpj(currentValue || '');
    }

    if (mask === 'cpfcnpj') {
      return mascaraCpfCnpj(currentValue || '');
    }

    return currentValue || '';
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value: string | number = event.target.value;

    const deveAplicarMaiuscula =
      type === 'text' &&
      !['cep', 'telefone', 'numero', 'porcentagem', 'dinheiro', 'numerov2'].includes(mask || '');

    if (typeof value === 'string' && deveAplicarMaiuscula) {
      value = value.toUpperCase();
    }

    if ((type === 'number' || mask === 'numero' || mask === 'numerov2') && !acceptFraction) {
      const regex = /^[0-9]*$/;
      if (!regex.test(value as string)) {
        const currentValue = getIn(formik.values, label);
        formik.setFieldValue(label, currentValue);
        return;
      }
    }

    if (mask === 'cep') {
      value = mascaraCep(value);
      formik.setFieldValue(label, value);
      return;
    }

    if (mask === 'telefone') {
      value = mascaraTelefone(value as string);
      formik.setFieldValue(label, value);
      return;
    }

    if (mask === 'cpf') {
      const cleanedValue = (value as string).replace(/\D/g, '');
      // Mantém o comportamento original de validação
      formik.setFieldValue(label, cleanedValue, true);
      
      // Quando o CPF tem 11 dígitos, garante que o campo seja marcado como touched
      // e força a validação para garantir que erros sejam exibidos imediatamente
      if (cleanedValue.length === 11) {
        setTimeout(() => {
          formik.setFieldTouched(label, true, true);
          // Validação explícita para garantir que a validação seja executada
          formik.validateField(label);
        }, 100);
      }
      return;
    }

    if (mask === 'numero' || mask === 'numerov2') {
      if (acceptFraction) {
        const regex = /^[0-9]*[.,]?[0-9]*$/;
        if (!regex.test(value as string)) {
          return;
        }
      }

      if (Number(value) < 0) {
        if (typeof value === 'number') {
          value = 0;
          formik.setFieldValue(label, value);
          return;
        } else {
          value = '0';
          formik.setFieldValue(label, value);
          return;
        }
      }
      formik.setFieldValue(label, value);
      return;
    }

    if (mask === 'porcentagem') {
      let valorNumerico = Number((value as string).replace('%', ''));
      if (valorNumerico > 100) {
        valorNumerico = 100;
      }
      if (valorNumerico < 0) {
        valorNumerico = 0;
      }
      formik.setFieldValue(label, String(valorNumerico).replace('%', ''));
      return;
    }

    if (mask === 'dinheiro') {
      const numeroValue = monetario_input(value as string);
      formik.setFieldValue(label, numeroValue);
      return;
    }

    if (mask === 'custom') {
      if (customMask) {
        value = customMask(value as string);
      }
      formik.setFieldValue(label, value);
      return;
    }

    if (type === 'number' && Number(value) < 0) {
      value = 0;
    }

    if (mask === 'data') {
      const valueData = value as string || '';
      const year = Number(valueData.split('-')[0]);
      const mount = valueData.split('-')[1];
      const day = valueData.split('-')[2];
      if (year > 2200) {

        value = `2200-${mount}-${day}`;
      }

      formik.setFieldValue(label, value);
      return;
    }

    formik.setFieldValue(label, value);
  };

  const handleGetError = () => {
    const currentValue = getIn(formik.errors, label);
    if (currentValue) {
      return currentValue;
    }
    return null;
  };

  const handleGetTouched = () => {
    return getIn(formik.touched, label);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    formik.setFieldTouched(label, true, true);
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((type === 'number' || mask === 'numero' || mask === 'numerov2') && !acceptFraction) {
      if (event.key === '.' || event.key === ',') {
        event.preventDefault();
        return;
      }
    }

    if (mask === 'porcentagem') {
      const currentValue = getIn(formik.values, label) || '';

      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (currentValue !== '' && currentValue !== null) {
          const numeroString = currentValue.toString();
          const novoNumero = numeroString.slice(0, -1);

          if (novoNumero === '') {
            formik.setFieldValue(label, '');
          } else {
            formik.setFieldValue(label, novoNumero);
          }

          event.preventDefault();
        }
      }
    } else if (mask === 'dinheiro') {
      const currentValue = getIn(formik.values, label) || 0;

      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (currentValue > 0) {
          const novoValor = Math.floor(currentValue * 100 / 10) / 100;
          formik.setFieldValue(label, novoValor);
          event.preventDefault();
        }
      }
    }
  };

  return (
    <>
      {version === 'primary' ? (
        <Box sx={{ width: width, mb: '16px' }}>
          <TextField
            id='beneficiarios-exclusivamente-municipio'
            label={labelTexto}
            variant='outlined'
            placeholder={placeholder}
            type={type}
            required={required}
            value={getDisplayValue()}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            InputProps={{ readOnly: disabled }}
            error={
              (formik.touched[label] && Boolean(formik.errors[label])) || handleGetError()
            }
            helperText={handleGetError()}
            inputProps={{
              maxLength: maxLength,
              step: acceptFraction ? '0.01' : '1',
            }}
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: '14px', fontWeight: 400 },
            }}
            size="small"
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '4px',
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                height: 'unset',
                boxSizing: 'border-box',
              },
            }}
          />
        </Box>
      ) : (
        <FormControl sx={{ width, display: 'flex', mb: '16px' }}>

          <TextField
            id={`${label}-input`}
            variant="outlined"
            type={type}
            required={required}
            label={labelTexto}
            value={getDisplayValue()}
            disabled={disabled}
            inputProps={{ maxLength }}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            error={handleGetTouched() && Boolean(handleGetError())}
            helperText={handleGetTouched() && handleGetError()}
            size="small"
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: '14px', fontWeight: 400 },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '4px',
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                height: 'unset',
                boxSizing: 'border-box',
              },
            }}
          />
        </FormControl>
      )}
    </>
  );
};

export default InputComLabelFormik;
